import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Building2, LogOut, MapPin, Trash2, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

// Venue Context Options
const VENUE_TYPES = [
  'Leisure Centre / Sports Complex',
  'Zoo / Animal Park',
  'Library',
  'Theatre / Performance Venue',
  'Museum / Heritage Site',
  'Science / Discovery Centre',
  'Theme Park / Attraction',
  'Soft Play / Family Entertainment',
  'Outdoor Activity Centre',
  'Community Centre',
  'Shopping Centre',
  'Other',
];

const PRIMARY_ENVIRONMENTS = [
  'Indoor',
  'Outdoor',
  'Mixed',
];

const STRUCTURAL_CHARACTERISTICS = [
  'Multi-level (stairs/lifts present)',
  'Large open areas (e.g. halls, concourses, stadium seating)',
  'Small enclosed areas (e.g. corridors, small rooms)',
  'Large site / long walking routes',
  'High visitor volume (busy / crowded environments)',
];

const ENTRY_MODELS = [
  'Walk-in / no booking',
  'Timed entry slots',
  'Booking required (online or phone)',
  'Membership / season pass',
  'Ticketed events / scheduled sessions',
];

const VENUE_SIZES = [
  'Small venue (under 1 hour visit)',
  'Medium venue (1–3 hour visit)',
  'Large venue (half-day visit)',
  'Very large venue (full-day attraction)',
];

const Dashboard = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [venueToDelete, setVenueToDelete] = useState(null);
  const [newVenue, setNewVenue] = useState({
    name: '',
    address: '',
    contact_info: '',
    website: '',
    description: '',
    venue_type: '',
    primary_environment: '',
    venue_size: '',
    structural_characteristics: [],
    entry_models: [],
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenues = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get('/venues');
      setVenues(response.data);
    } catch (error) {
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVenue = async (e) => {
    e.preventDefault();

    if (venues.length >= 3) {
      toast.error('Maximum 3 venues allowed per account');
      return;
    }

    // Validate required fields
    if (!newVenue.primary_environment) {
      toast.error('Please select a Primary Environment');
      return;
    }

    try {
      const response = await api.post('/venues', newVenue);
      const newVenueId = response.data.venue_id;
      setDialogOpen(false);
      setNewVenue({ 
        name: '', 
        address: '', 
        contact_info: '', 
        website: '', 
        description: '',
        venue_type: '',
        primary_environment: '',
        venue_size: '',
        structural_characteristics: [],
        entry_models: [],
      });
      toast.success('Venue profile created successfully. Now add areas to your venue.');
      // Navigate to the new venue page
      navigate(`/venue/${newVenueId}`);
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to create venue';
      toast.error(errorMessage);
    }
  };

  const toggleStructuralCharacteristic = (char) => {
    setNewVenue(prev => ({
      ...prev,
      structural_characteristics: prev.structural_characteristics.includes(char)
        ? prev.structural_characteristics.filter(c => c !== char)
        : [...prev.structural_characteristics, char]
    }));
  };

  const toggleEntryModel = (model) => {
    setNewVenue(prev => ({
      ...prev,
      entry_models: prev.entry_models.includes(model)
        ? prev.entry_models.filter(m => m !== model)
        : [...prev.entry_models, model]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteVenue = (venue) => {
    setVenueToDelete(venue);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVenue = async () => {
    if (!venueToDelete) return;

    try {
      await api.delete(`/venues/${venueToDelete.venue_id}`);
      setVenues(venues.filter(v => v.venue_id !== venueToDelete.venue_id));
      toast.success(`Venue "${venueToDelete.name}" deleted`);
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to delete venue';
      toast.error(errorMsg);
    } finally {
      setDeleteDialogOpen(false);
      setVenueToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" data-testid="dashboard-title">
              Your Venues
            </h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.name}. You have {venues.length}/3 venues.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {user?.is_admin && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="rounded-full"
                data-testid="admin-link"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="rounded-full"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {venues.length === 0 ? (
            <div className="col-span-full">
              <div className="bg-card rounded-xl border border-border/50 shadow-sm p-8 text-center relative z-10">
                <div className="bg-primary/10 rounded-full p-4 inline-block mb-4">
                  <Building2 className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-2">No venues created yet</h3>
                <p className="text-muted-foreground mb-6">
                  Create your first venue profile to get started.
                </p>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="rounded-full relative z-20 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  data-testid="empty-state-create-venue"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Venue
                </Button>
              </div>
            </div>
          ) : (
            venues.map((venue, index) => (
            <motion.div
              key={venue.venue_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative group"
            >
              <div
                className="bg-card rounded-xl border border-border/50 shadow-sm hover:shadow-md transition-all duration-200 p-6 cursor-pointer h-full"
                onClick={() => navigate(`/venue/${venue.venue_id}`)}
                data-testid={`venue-card-${venue.venue_id}`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="bg-primary/10 rounded-lg p-3">
                    <Building2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg tracking-tight">{venue.name}</h3>
                    {venue.address && (
                      <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span>{venue.address}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteVenue(venue);
                    }}
                    data-testid={`delete-venue-${venue.venue_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>

                {venue.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{venue.description}</p>
                )}

                <div className="mt-4 pt-4 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Click to manage areas and submissions
                  </span>
                </div>
              </div>
            </motion.div>
          ))
          )}

          {venues.length > 0 && venues.length < 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: venues.length * 0.1 }}
            >
              <div
                onClick={() => setDialogOpen(true)}
                className="bg-card rounded-xl border-2 border-dashed border-border hover:border-primary/50 transition-colors duration-200 p-6 cursor-pointer h-full flex flex-col items-center justify-center min-h-[200px] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                data-testid="create-venue-button"
              >
                <div className="bg-primary/10 rounded-full p-4 mb-3">
                  <Plus className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-semibold text-lg mb-1">Add New Venue</h3>
                <p className="text-sm text-muted-foreground text-center">
                  Create a new venue to start building your sensory guide
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Create Venue Dialog - rendered separately so it works for empty state too */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Venue</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateVenue} className="space-y-4" data-testid="create-venue-form-modal">
            {/* Venue Information Section */}
            <div className="space-y-4">
              <h4 className="font-semibold text-primary border-b pb-2">Venue Information</h4>
              
              <div className="space-y-2">
                <Label htmlFor="venue-name-modal">Venue Name *</Label>
                <Input
                  id="venue-name-modal"
                  placeholder="e.g., The Quiet Cafe"
                  value={newVenue.name}
                  onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                  required
                  data-testid="venue-name-input-modal"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue-address-modal">Address</Label>
                <Input
                  id="venue-address-modal"
                  placeholder="123 Main Street, City"
                  value={newVenue.address}
                  onChange={(e) => setNewVenue({ ...newVenue, address: e.target.value })}
                  data-testid="venue-address-input-modal"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue-description-modal">About / Description</Label>
                <Textarea
                  id="venue-description-modal"
                  placeholder="Brief description of your venue"
                  value={newVenue.description}
                  onChange={(e) => setNewVenue({ ...newVenue, description: e.target.value })}
                  data-testid="venue-description-input-modal"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  This information helps generate your sensory accessibility report.
                </p>
              </div>
            </div>

            {/* Venue Context Section */}
            <div className="pt-4 border-t space-y-4">
              <h4 className="font-semibold text-primary border-b pb-2">Venue Context</h4>
              
              <div className="space-y-2">
                <Label htmlFor="venue-type-modal">Venue Type <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Select 
                  value={newVenue.venue_type} 
                  onValueChange={(value) => setNewVenue({ ...newVenue, venue_type: value })}
                >
                  <SelectTrigger className="h-11" data-testid="venue-type-select-modal">
                    <SelectValue placeholder="Select venue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENUE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="primary-environment-modal">Primary Environment *</Label>
                <Select 
                  value={newVenue.primary_environment} 
                  onValueChange={(value) => setNewVenue({ ...newVenue, primary_environment: value })}
                >
                  <SelectTrigger className="h-11" data-testid="primary-environment-select-modal">
                    <SelectValue placeholder="Select environment" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIMARY_ENVIRONMENTS.map(env => (
                      <SelectItem key={env} value={env}>{env}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Is the venue mainly indoor, outdoor, or a mix of both?
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="venue-size-modal">Venue Size <span className="text-xs text-muted-foreground">(Optional)</span></Label>
                <Select 
                  value={newVenue.venue_size} 
                  onValueChange={(value) => setNewVenue({ ...newVenue, venue_size: value })}
                >
                  <SelectTrigger className="h-11" data-testid="venue-size-select-modal">
                    <SelectValue placeholder="Select venue size" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENUE_SIZES.map(size => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Structural Characteristics <span className="text-xs text-muted-foreground">(Optional)</span></Label>
                <div className="grid grid-cols-1 gap-2 p-3 border rounded-lg bg-secondary/20">
                  {STRUCTURAL_CHARACTERISTICS.map(char => (
                    <div key={char} className="flex items-center space-x-2">
                      <Checkbox
                        id={`struct-modal-${char}`}
                        checked={newVenue.structural_characteristics.includes(char)}
                        onCheckedChange={() => toggleStructuralCharacteristic(char)}
                      />
                      <label htmlFor={`struct-modal-${char}`} className="text-sm cursor-pointer">
                        {char}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Entry Model(s) <span className="text-xs text-muted-foreground">(Optional)</span></Label>
                <div className="grid grid-cols-1 gap-2 p-3 border rounded-lg bg-secondary/20">
                  {ENTRY_MODELS.map(model => (
                    <div key={model} className="flex items-center space-x-2">
                      <Checkbox
                        id={`entry-modal-${model}`}
                        checked={newVenue.entry_models.includes(model)}
                        onCheckedChange={() => toggleEntryModel(model)}
                      />
                      <label htmlFor={`entry-modal-${model}`} className="text-sm cursor-pointer">
                        {model}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-full font-medium"
              data-testid="venue-submit-button-modal"
            >
              Create Venue Profile
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Venue?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{venueToDelete?.name}"</strong>?
              <span className="block mt-2 text-destructive">
                This will permanently remove all areas, sensory assessments, and submissions for this venue.
              </span>
              <span className="block mt-2">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteVenue}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-venue"
            >
              Delete Venue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
